import { Target, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { localDay, shiftDay } from '../../shared/calendar';
import type { LocalRecord, Note, Task, WorkspaceSettingsValue } from '../../shared/model';
import {
  type Goal,
  goalProgress,
  type Person,
  type Progress,
  projectChoices,
  type Review,
  taskPersonName,
  weekStart,
} from '../../shared/productivity';
import { isAvailableRecord } from '../../shared/trash';

import { workspace } from '../lib/engine';
import { StatusBadge, TaskBadges } from './StatusBadge';
import {
  ActionIconButton,
  Button,
  Disclosure,
  InlineCreate,
  Input,
  Modal,
  PageHeader,
  Select,
  Textarea,
} from './ui';

const run = (action: Promise<unknown>) => void action.catch(() => {});

export function TaskContext({
  record,
  records,
  expanded = false,
}: {
  record: LocalRecord;
  records: LocalRecord[];
  expanded?: boolean;
}) {
  const task = record.value as Task;
  const waitingPerson = taskPersonName(task, records);

  const [draft, setDraft] = useState(task),
    [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setDraft(task);
    }
  }, [record.value, dirty]);

  function updateDraft(value: Task) {
    setDirty(true);
    setDraft(value);
  }

  return (
    <Disclosure
      className="task-context"
      open={expanded}
      summary={`Deadline, estimate & context${task.dueDate ? ` · ${task.dueDate}` : ''}${
        waitingPerson ? ` · ${waitingPerson} is waiting` : ''
      }`}
    >
      <form
        onSubmit={e => {
          e.preventDefault();

          run(
            workspace
              .save(record.id, 'task', {
                ...task,
                title: draft.title,
                dueDate: draft.dueDate,
                estimateMinutes: draft.estimateMinutes,
                personId: draft.personId,
                projectId: draft.projectId,
                areaId: draft.areaId,
              })
              .then(() => setDirty(false)),
          );
        }}
      >
        <label>
          Task title
          <Input
            required
            maxLength={500}
            value={draft.title}
            onChange={e => updateDraft({ ...draft, title: e.target.value })}
          />
        </label>
        <div className="field-grid">
          <label>
            Deadline
            <Input
              type="date"
              value={draft.dueDate ?? ''}
              onInput={e => updateDraft({ ...draft, dueDate: e.currentTarget.value || null })}
            />
          </label>
          <label>
            Estimate (minutes)
            <Input
              type="number"
              min={1}
              max={10080}
              value={draft.estimateMinutes ?? ''}
              onChange={e =>
                updateDraft({
                  ...draft,
                  estimateMinutes: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </label>
        </div>
        <label>
          Person
          <Select
            value={draft.personId ?? ''}
            onChange={e => updateDraft({ ...draft, personId: e.target.value || null })}
          >
            <option value="">None</option>
            {records
              .filter(r => r.kind === 'person')
              .map(r => (
                <option key={r.id} value={r.id}>
                  {(r.value as Person).name}
                </option>
              ))}
          </Select>
          <span className="small-muted">This person is waiting for you to finish the task.</span>
        </label>
        <label>
          Project
          <Select
            value={draft.projectId ?? ''}
            onChange={e => updateDraft({ ...draft, projectId: e.target.value || null })}
          >
            <option value="">None</option>
            {projectChoices(records)
              .filter(project => !project.nested || project.id === draft.projectId)
              .map(project => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
          </Select>
        </label>
        <label>
          Area
          <Select
            value={draft.areaId ?? ''}
            onChange={e => updateDraft({ ...draft, areaId: e.target.value || null })}
          >
            <option value="">None</option>
            {records
              .filter(
                r =>
                  r.kind === 'note' && isAvailableRecord(r) && (r.value as Note).area === 'Areas',
              )
              .map(r => (
                <option key={r.id} value={r.id}>
                  {(r.value as Note).title}
                </option>
              ))}
          </Select>
        </label>
        <div className="button-row">
          <Button variant="secondary">Save context</Button>
          <ActionIconButton
            action="trash"
            label="Move task to trash"
            type="button"
            disabled={Boolean(task.focus?.sessionOpen)}
            onClick={() =>
              run(workspace.save(record.id, 'task', { ...task, trashed: true, today: false }))
            }
          />
        </div>
      </form>
    </Disclosure>
  );
}

export function GoalsPage({ records }: { records: LocalRecord[] }) {
  const [selected, setSelected] = useState(''),
    [creating, setCreating] = useState(false),
    [showArchived, setShowArchived] = useState(false);

  const goals = records.filter(
    r => r.kind === 'goal' && (showArchived || (r.value as Goal).status !== 'archived'),
  );

  const record = goals.find(r => r.id === selected) ?? goals[0];

  return (
    <main className="productivity-page">
      <PageHeader
        eyebrow="DIRECTION, WITHOUT A STREAK"
        title="Goals"
        actions={
          <ActionIconButton action="goal" label="New goal" onClick={() => setCreating(true)} />
        }
      />
      <label className="inline-check">
        <Input
          type="checkbox"
          checked={showArchived}
          onChange={e => setShowArchived(e.target.checked)}
        />
        Show archived
      </label>
      {!goals.length ? (
        <div className="empty-state">
          <Target size={32} />
          <h2>{showArchived ? 'No goals yet.' : 'No active goals.'}</h2>
          <p>Choose a concrete outcome: a first gym visit, or making an appointment.</p>
          <ActionIconButton action="goal" label="New goal" onClick={() => setCreating(true)} />
        </div>
      ) : (
        <div className="productivity-layout">
          <aside className="record-list">
            {goals.map(r => {
              const goal = r.value as Goal;

              return (
                <button
                  type="button"
                  data-ui="specialized"
                  key={r.id}
                  className={`record-card ${record?.id === r.id ? 'selected' : ''}`}
                  onClick={() => setSelected(r.id)}
                >
                  <strong>{goal.title}</strong>
                  <span>
                    {goalProgress(r.id, records)} / {goal.target} {goal.unit}
                  </span>
                  <StatusBadge
                    tone={
                      goal.status === 'completed'
                        ? 'success'
                        : goal.status === 'active'
                          ? 'focus'
                          : 'neutral'
                    }
                  >
                    {goal.status === 'completed'
                      ? 'Completed'
                      : goal.status === 'active'
                        ? 'Active'
                        : 'Archived'}
                  </StatusBadge>
                </button>
              );
            })}
          </aside>
          {record && (
            <GoalDetail
              key={`${record.id}:${record.localVersion}`}
              record={record}
              records={records}
            />
          )}
        </div>
      )}
      {creating && (
        <GoalForm
          records={records}
          close={() => setCreating(false)}
          saved={id => {
            setSelected(id);
            setCreating(false);
          }}
        />
      )}
    </main>
  );
}

function GoalDetail({ record, records }: { record: LocalRecord; records: LocalRecord[] }) {
  const goal = record.value as Goal,
    total = goalProgress(record.id, records),
    weekly = goalProgress(record.id, records, weekStart());

  const [editing, setEditing] = useState(false),
    [amount, setAmount] = useState('1'),
    [date, setDate] = useState(localDay()),
    [note, setNote] = useState(''),
    [taskId, setTaskId] = useState('');

  const entries = records
    .filter(r => r.kind === 'progress' && (r.value as Progress).goalId === record.id)
    .sort((a, b) => (b.value as Progress).date.localeCompare((a.value as Progress).date));

  return (
    <section className="detail-card">
      <div className="section-heading">
        <h2>{goal.title}</h2>
        <Button variant="ghost" onClick={() => setEditing(true)}>
          Edit goal
        </Button>
      </div>
      <progress aria-label="Goal progress" max={goal.target} value={Math.min(total, goal.target)} />
      <p>
        {total} of {goal.target} {goal.unit} · {goal.status}
        {goal.completedAt ? ` on ${new Date(goal.completedAt).toLocaleDateString()}` : ''}
      </p>
      {goal.weeklyTarget && (
        <p>
          This week: {weekly} / {goal.weeklyTarget} {goal.unit}. A guide, with room to adjust.
        </p>
      )}
      <h3>Linked tasks</h3>
      {goal.taskIds.length ? (
        goal.taskIds.map(id => {
          const task = records.find(r => r.id === id)?.value as Task;

          return (
            <p key={id}>
              {task?.done ? '✓' : '○'} {task?.title ?? 'Deleted task'}{' '}
              <small>
                {task?.trashed
                  ? 'Removed task · earned credit retained'
                  : 'One credit when completed'}
              </small>
            </p>
          );
        })
      ) : (
        <p>No linked tasks yet.</p>
      )}
      <form
        className="stack-form"
        onSubmit={async e => {
          e.preventDefault();

          try {
            await workspace.save(crypto.randomUUID(), 'progress', {
              goalId: record.id,
              amount: Number(amount),
              date,
              taskId: taskId || null,
              note,
              reversed: false,
            });

            setNote('');
          } catch {}
        }}
      >
        <h3>Record progress</h3>
        <div className="field-grid">
          <label>
            Amount
            <Input
              required
              type="number"
              min="0.001"
              step="any"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </label>
          <label>
            Progress date
            <Input
              required
              type="date"
              value={date}
              onInput={e => setDate(e.currentTarget.value)}
            />
          </label>
        </div>
        <label>
          Source
          <Select value={taskId} onChange={e => setTaskId(e.target.value)}>
            <option value="">Manual progress</option>
            {records
              .filter(r => r.kind === 'task' && isAvailableRecord(r) && (r.value as Task).done)
              .map(r => (
                <option key={r.id} value={r.id}>
                  {(r.value as Task).title}
                </option>
              ))}
          </Select>
        </label>
        {taskId && (
          <small>
            A task entry replaces its one-credit default. Reopening the task removes its credit.
          </small>
        )}
        <label>
          Progress note
          <Input value={note} onChange={e => setNote(e.target.value)} maxLength={5000} />
        </label>
        <Button variant="primary">Record progress</Button>
      </form>
      <h3>Progress history</h3>
      {entries.map(r => {
        const p = r.value as Progress;

        return (
          <div className="history-row" key={r.id}>
            <span>
              {p.date} · {p.amount} {goal.unit}
              {p.reversed ? ' · reversed' : ''}
              <small>
                {p.note}
                {p.taskId
                  ? ` · ${(records.find(t => t.id === p.taskId)?.value as Task)?.title}`
                  : ''}
              </small>
            </span>
            <ActionIconButton
              action={p.reversed ? 'restore' : 'reverse'}
              label={p.reversed ? 'Restore entry' : 'Reverse entry'}
              onClick={() => run(workspace.save(r.id, 'progress', { ...p, reversed: !p.reversed }))}
            />
          </div>
        );
      })}
      {editing && (
        <GoalForm
          record={record}
          records={records}
          close={() => setEditing(false)}
          saved={() => setEditing(false)}
        />
      )}
    </section>
  );
}

function GoalForm({
  record,
  records,
  close,
  saved,
}: {
  record?: LocalRecord;
  records: LocalRecord[];
  close: () => void;
  saved: (id: string) => void;
}) {
  const [draft, setDraft] = useState<Goal>(
    (record?.value as Goal) ?? {
      title: '',
      target: 1,
      unit: 'steps',
      weeklyTarget: null,
      taskIds: [],
      status: 'active',
      completedAt: null,
      noteId: null,
    },
  );

  return (
    <Modal
      open
      onClose={close}
      title={record ? 'Edit goal' : 'New goal'}
      onSubmit={async e => {
        e.preventDefault();
        const id = record?.id ?? crypto.randomUUID();

        try {
          await workspace.save(id, 'goal', {
            ...draft,
            completedAt:
              draft.status === 'completed' ? (draft.completedAt ?? new Date().toISOString()) : null,
          });

          saved(id);
        } catch {}
      }}
    >
      <label>
        Outcome
        <Input
          required
          value={draft.title}
          onChange={e => setDraft({ ...draft, title: e.target.value })}
        />
      </label>
      <div className="field-grid">
        <label>
          Target
          <Input
            required
            type="number"
            min="0.001"
            step="any"
            value={draft.target}
            onChange={e => setDraft({ ...draft, target: Number(e.target.value) })}
          />
        </label>
        <label>
          Unit
          <Input value={draft.unit} onChange={e => setDraft({ ...draft, unit: e.target.value })} />
        </label>
      </div>
      <label>
        Flexible weekly target
        <Input
          type="number"
          min="0.001"
          step="any"
          value={draft.weeklyTarget ?? ''}
          onChange={e =>
            setDraft({ ...draft, weeklyTarget: e.target.value ? Number(e.target.value) : null })
          }
        />
      </label>
      <fieldset>
        <legend>Credit these tasks once when completed</legend>
        {!records.some(r => r.kind === 'task' && isAvailableRecord(r)) && (
          <p>No tasks available. Create a task or restore one from Trash.</p>
        )}
        {draft.taskIds.some(id => records.some(r => r.id === id && !isAvailableRecord(r))) && (
          <p className="muted">Existing links to removed tasks retain their earned credit.</p>
        )}
        {records
          .filter(r => r.kind === 'task' && isAvailableRecord(r))
          .map(r => (
            <label className="inline-check" key={r.id}>
              <Input
                type="checkbox"
                checked={draft.taskIds.includes(r.id)}
                onChange={e =>
                  setDraft({
                    ...draft,
                    taskIds: e.target.checked
                      ? [...draft.taskIds, r.id]
                      : draft.taskIds.filter(id => id !== r.id),
                  })
                }
              />
              {(r.value as Task).title}
            </label>
          ))}
      </fieldset>
      <label>
        Working note
        <Select
          value={draft.noteId ?? ''}
          onChange={e => setDraft({ ...draft, noteId: e.target.value || null })}
        >
          <option value="">None</option>
          {records
            .filter(r => r.kind === 'note' && isAvailableRecord(r))
            .map(r => (
              <option key={r.id} value={r.id}>
                {(r.value as Note).title}
              </option>
            ))}
        </Select>
      </label>
      <label>
        Status
        <Select
          value={draft.status}
          onChange={e => setDraft({ ...draft, status: e.target.value as Goal['status'] })}
        >
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </Select>
      </label>
      <p>Reaching the target marks this goal complete. No recurring commitment is created.</p>
      <div className="button-row">
        <Button variant="primary">Save goal</Button>
        <Button variant="secondary" type="button" onClick={close}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

export function ReviewsPage(props: { records: LocalRecord[]; openNotes: () => void }) {
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => tick(n => n + 1), 60000);

    return () => clearInterval(timer);
  }, []);

  const week = weekStart();

  return <ReviewWeek key={week} week={week} {...props} />;
}

function ReviewWeek({
  week,
  records,
  openNotes,
}: {
  week: string;
  records: LocalRecord[];
  openNotes: () => void;
}) {
  const id = `ae100000-0000-4000-8000-${week.replaceAll('-', '').padStart(12, '0')}`;
  const existing = records.find(r => r.kind === 'review' && (r.value as Review).week === week);

  const [draft, setDraft] = useState<Review>(
    (existing?.value as Review) ?? { week, reflection: '', decisions: '', completedAt: null },
  );

  const history = records
    .filter(r => r.kind === 'review' && (r.value as Review).completedAt)
    .sort((a, b) => (b.value as Review).week.localeCompare((a.value as Review).week));

  const weeks = Array.from({ length: 8 }, (_, index) => shiftDay(week, -7 * index));

  const tasks = records.filter(
    r => r.kind === 'task' && !(r.value as Task).done && !(r.value as Task).trashed,
  );

  return (
    <main className="productivity-page review-page">
      <PageHeader
        eyebrow={`WEEK OF ${week}`}
        title="A lighter week ahead."
        description="Start with this week. Missed reviews stay as gaps; there is no backlog to repay."
      />
      <section className="detail-card">
        <h2>1. Review goal progress</h2>
        {records
          .filter(r => r.kind === 'goal' && (r.value as Goal).status !== 'archived')
          .map(r => (
            <p key={r.id}>
              <strong>{(r.value as Goal).title}</strong> · {goalProgress(r.id, records, week)}{' '}
              {(r.value as Goal).unit} this week · {goalProgress(r.id, records)} /{' '}
              {(r.value as Goal).target} total
            </p>
          ))}
        <p>
          {
            records.filter(
              r =>
                r.kind === 'task' &&
                (r.value as Task).done &&
                (r.value as Task).completedOn! >= week &&
                (r.value as Task).completedOn! < shiftDay(week, 7),
            ).length
          }{' '}
          tasks completed this week.
        </p>
        {Object.entries(
          (records.find(r => r.kind === 'settings')?.value as WorkspaceSettingsValue | undefined)
            ?.dailyChecks ?? {},
        )
          .filter(([day]) => day >= week && day < shiftDay(week, 7))
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, feeling]) => (
            <p key={day}>
              {day} ·{' '}
              {feeling === 'overloaded'
                ? 'Too much'
                : feeling === 'manageable'
                  ? 'Manageable'
                  : 'Mixed'}
            </p>
          ))}
        <label>
          What helped, and what needs adjusting?
          <Textarea
            value={draft.reflection}
            onChange={e => {
              const next = { ...draft, reflection: e.target.value };

              setDraft(next);
              run(workspace.save(existing?.id ?? id, 'review', next));
            }}
          />
        </label>
      </section>
      <section className="detail-card">
        <h2>2. Organize notes</h2>
        <p>
          {
            records.filter(
              r => r.kind === 'note' && isAvailableRecord(r) && (r.value as Note).area === 'Inbox',
            ).length
          }{' '}
          notes in Inbox. Keep what is useful and give it a home.
        </p>
        <Button variant="secondary" onClick={openNotes}>
          Open Inbox
        </Button>
      </section>
      <section className="detail-card">
        <h2>3. Drop or renegotiate commitments</h2>
        {tasks.map(r => {
          const task = r.value as Task;

          return (
            <div className="history-row" key={r.id}>
              <span>
                {task.title}
                <TaskBadges record={r} records={records} />
              </span>
              <div className="button-row field-actions">
                <label>
                  New deadline
                  <Input
                    aria-label={`New deadline for ${task.title}`}
                    type="date"
                    value={task.dueDate ?? ''}
                    onInput={e =>
                      run(
                        workspace.save(r.id, 'task', {
                          ...task,
                          dueDate: e.currentTarget.value || null,
                        }),
                      )
                    }
                  />
                </label>
                <ActionIconButton
                  action={task.dropped ? 'restore' : 'drop'}
                  label={task.dropped ? 'Restore commitment' : 'Drop commitment'}
                  disabled={task.focus?.sessionOpen}
                  title={task.focus?.sessionOpen ? 'End the focus session first' : undefined}
                  onClick={() =>
                    run(
                      workspace.save(r.id, 'task', {
                        ...task,
                        dropped: !task.dropped,
                        today: false,
                      }),
                    )
                  }
                />
              </div>
            </div>
          );
        })}
        <label>
          What will you drop or renegotiate?
          <Textarea
            value={draft.decisions}
            onChange={e => {
              const next = { ...draft, decisions: e.target.value };

              setDraft(next);
              run(workspace.save(existing?.id ?? id, 'review', next));
            }}
          />
        </label>
      </section>
      <Button
        variant="primary"
        onClick={() => {
          const next = { ...draft, completedAt: new Date().toISOString() };

          setDraft(next);
          run(workspace.save(existing?.id ?? id, 'review', next));
        }}
      >
        {draft.completedAt ? 'Update completed review' : 'Complete this week’s review'}
      </Button>
      <section className="detail-card">
        <h2>Recent weeks</h2>
        {weeks.map(w => {
          const r = history.find(r => (r.value as Review).week === w);

          return (
            <Disclosure
              key={w}
              summary={
                <>
                  {w}{' '}
                  <StatusBadge tone={r ? 'success' : w === week ? 'priority' : 'neutral'}>
                    {r ? 'Reviewed' : w === week ? 'Current week' : 'No review recorded'}
                  </StatusBadge>
                </>
              }
            >
              {r && (
                <>
                  <p className="preserve-lines">{(r.value as Review).reflection}</p>
                  <p className="preserve-lines">{(r.value as Review).decisions}</p>
                </>
              )}
            </Disclosure>
          );
        })}
      </section>
    </main>
  );
}

export function PeoplePage({ records }: { records: LocalRecord[] }) {
  const [selected, setSelected] = useState(''),
    [name, setName] = useState('');

  const people = records.filter(r => r.kind === 'person');
  const record = people.find(r => r.id === selected) ?? people[0];

  return (
    <main className="productivity-page">
      <PageHeader eyebrow="PEOPLE & PROMISES" title="People" />
      <InlineCreate
        label="New person name"
        value={name}
        onChange={setName}
        placeholder="Add a person…"
        action="Add person"
        icon="person"
        onSubmit={async e => {
          e.preventDefault();

          if (!name.trim()) {
            return;
          }

          const id = crypto.randomUUID();

          try {
            await workspace.save(id, 'person', {
              name: name.trim(),
              contact: '',
              notes: '',
              gifts: [],
              archived: false,
            });

            setName('');
            setSelected(id);
          } catch {}
        }}
      />
      {!people.length ? (
        <div className="empty-state">
          <Users size={32} />
          <h2>No one here yet.</h2>
          <p>Add the people you owe something, or want to remember.</p>
        </div>
      ) : (
        <div className="productivity-layout">
          <aside className="record-list">
            {people.map(r => (
              <button
                type="button"
                data-ui="specialized"
                className={`record-card ${r.id === record?.id ? 'selected' : ''}`}
                key={r.id}
                onClick={() => setSelected(r.id)}
              >
                {(r.value as Person).name}
                {(r.value as Person).archived ? ' · archived' : ''}
              </button>
            ))}
          </aside>
          {record && <PersonDetail key={`${record.id}:${record.localVersion}`} record={record} />}
        </div>
      )}
    </main>
  );
}

function PersonDetail({ record }: { record: LocalRecord }) {
  const [draft, setDraft] = useState(record.value as Person),
    [gift, setGift] = useState('');

  return (
    <form
      className="detail-card stack-form"
      onSubmit={e => {
        e.preventDefault();
        run(workspace.save(record.id, 'person', draft));
      }}
    >
      <label>
        Name
        <Input
          required
          value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
        />
      </label>
      <label>
        Contact details
        <Input
          value={draft.contact}
          onChange={e => setDraft({ ...draft, contact: e.target.value })}
        />
      </label>
      <label>
        Context
        <Textarea
          value={draft.notes}
          onChange={e => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>
      <h3>Gift ideas & history</h3>
      {draft.gifts.map(g => (
        <div className="gift-row" key={g.id}>
          <label>
            Gift
            <Input
              required
              value={g.title}
              onChange={e =>
                setDraft({
                  ...draft,
                  gifts: draft.gifts.map(item =>
                    item.id === g.id ? { ...item, title: e.target.value } : item,
                  ),
                })
              }
            />
          </label>
          <label>
            Status
            <Select
              value={g.status}
              onChange={e =>
                setDraft({
                  ...draft,
                  gifts: draft.gifts.map(item =>
                    item.id === g.id
                      ? { ...item, status: e.target.value as 'idea' | 'given' }
                      : item,
                  ),
                })
              }
            >
              <option value="idea">Idea</option>
              <option value="given">Given</option>
            </Select>
          </label>
          <label>
            Date
            <Input
              type="date"
              value={g.date ?? ''}
              onInput={e => {
                const date = e.currentTarget.value;

                setDraft({
                  ...draft,
                  gifts: draft.gifts.map(item =>
                    item.id === g.id ? { ...item, date: date || null } : item,
                  ),
                });
              }}
            />
          </label>
          <label>
            Gift notes
            <Input
              value={g.notes}
              onChange={e =>
                setDraft({
                  ...draft,
                  gifts: draft.gifts.map(item =>
                    item.id === g.id ? { ...item, notes: e.target.value } : item,
                  ),
                })
              }
            />
          </label>
          <ActionIconButton
            action="trash"
            label="Remove gift"
            type="button"
            onClick={() =>
              setDraft({ ...draft, gifts: draft.gifts.filter(item => item.id !== g.id) })
            }
          />
        </div>
      ))}
      <div className="input-action">
        <Input
          aria-label="New gift idea"
          value={gift}
          onChange={e => setGift(e.target.value)}
          placeholder="A gift idea…"
        />
        <ActionIconButton
          action="add"
          label="Add idea"
          type="button"
          onClick={() => {
            if (gift.trim()) {
              setDraft({
                ...draft,
                gifts: [
                  ...draft.gifts,
                  {
                    id: crypto.randomUUID(),
                    title: gift.trim(),
                    date: null,
                    status: 'idea',
                    notes: '',
                  },
                ],
              });

              setGift('');
            }
          }}
        />
      </div>
      <label className="inline-check">
        <Input
          type="checkbox"
          checked={draft.archived}
          onChange={e => setDraft({ ...draft, archived: e.target.checked })}
        />
        Archived
      </label>
      <Button variant="primary">Save person</Button>
    </form>
  );
}
